import { useEffect, useState } from 'react'

import { ensureEpisodePages } from '../../lib/comicPages'
import type { ComicEpisode, ComicEpisodeEditPatch } from '../../lib/comicTypes'
import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { ChromeComposerTextarea } from '../ChromeComposerTextarea'
import { chromeInputClass } from '../ChromeSurface'
import { ThemeModal } from '../modal'
import { SettingsGroup } from '../SettingsHierarchy'

const TITLE_MAX = 40
const TEXT_MAX = 1024

type Props = {
  open: boolean
  angular: boolean
  episode: ComicEpisode | null
  onClose: () => void
  onSave: (patch: ComicEpisodeEditPatch) => void
}

export function ComicEpisodeEditModal({ open, angular, episode, onClose, onSave }: Props) {
  const [draftTitle, setDraftTitle] = useState('')
  const [draftSummary, setDraftSummary] = useState('')
  const [draftPlotAdvance, setDraftPlotAdvance] = useState('')

  useEffect(() => {
    if (!open || !episode) return
    setDraftTitle(episode.title)
    setDraftSummary(episode.summary ?? '')
    setDraftPlotAdvance(episode.plotAdvance ?? '')
  }, [open, episode])

  const pageCount = episode ? ensureEpisodePages(episode).pages.length : 0
  const plannedLabel =
    episode?.plannedPageCount && episode.plannedPageCount > 0
      ? ` · 规划 ${episode.plannedPageCount} 页`
      : ''
  const canSave = draftTitle.trim().length > 0

  const handleSave = () => {
    if (!canSave) return
    onSave({
      title: draftTitle.trim(),
      summary: draftSummary.trim() || undefined,
      plotAdvance: draftPlotAdvance.trim() || undefined,
    })
    onClose()
  }

  return (
    <ThemeModal
      open={open}
      onClose={onClose}
      title="编辑话次"
      angular={angular}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <ChromeToolbarButton type="button" onClick={onClose}>
            取消
          </ChromeToolbarButton>
          <ChromePrimaryButton density="compact" type="button" onClick={handleSave} disabled={!canSave}>
            保存
          </ChromePrimaryButton>
        </div>
      }
    >
      <div className="space-y-4">
        <section className="space-y-3">
          <label className="block text-sm font-semibold text-ink">
            话次名称
            <input
              className={chromeInputClass(angular)}
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              maxLength={TITLE_MAX}
              autoFocus
            />
          </label>
          {episode ? (
            <p className="text-xs tabular-nums text-ink-soft">
              当前 {pageCount} 页{plannedLabel}
            </p>
          ) : null}
        </section>
        <SettingsGroup
          angular={angular}
          title="工作流目标"
          description="不参与普通生图提示词设定"
        >
          <label className="block text-sm font-semibold text-ink">
            摘要
            <ChromeComposerTextarea
              angular={angular}
              variant="form"
              value={draftSummary}
              onChange={(e) => setDraftSummary(e.target.value)}
              placeholder="本话剧情概要"
              maxLength={TEXT_MAX}
              textareaClassName="min-h-[5rem]"
            />
          </label>
          <label className="block text-sm font-semibold text-ink">
            剧情推进
            <ChromeComposerTextarea
              angular={angular}
              variant="form"
              value={draftPlotAdvance}
              onChange={(e) => setDraftPlotAdvance(e.target.value)}
              placeholder="本话叙事目标或情节节点"
              maxLength={TEXT_MAX}
              textareaClassName="min-h-[5rem]"
            />
          </label>
        </SettingsGroup>
      </div>
    </ThemeModal>
  )
}
